import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SplitButton,
  FieldText, FieldSelect, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

// Aynı backend ucu (CoolerFaultReportsController). OData DEĞİL; REST/ApiResponse
// zarfı. GET-by-id YOK → tek kayıt önce location.state.row, yoksa listeden (take=500)
// id ile bulunur. Create/Delete YOK (mobil push tek kaynak) → "Kaydet ve Yeni"
// menüsü ve "Sil" aksiyonu KONULMAZ. Yalnız TrackingNo + Status editlenir (PUT).
const API_BASE = '/api/cooler-fault-reports';
const FETCH_TAKE = 500;

const STATUS_META = {
  Open: { label: 'Açık', tone: 'wait' },
  InProgress: { label: 'Devam', tone: 'wait' },
  Resolved: { label: 'Çözüldü', tone: 'ok' },
  Cancelled: { label: 'İptal', tone: 'neutral' },
};

const STATUS_OPTIONS = [
  { value: 'Open', label: 'Açık' },
  { value: 'InProgress', label: 'Devam' },
  { value: 'Resolved', label: 'Çözüldü' },
  { value: 'Cancelled', label: 'İptal' },
];

const URGENCY_META = {
  CRITICAL: { label: 'Kritik', tone: 'bad' },
  HIGH: { label: 'Yüksek', tone: 'wait' },
  NORMAL: { label: 'Normal', tone: 'neutral' },
  LOW: { label: 'Düşük', tone: 'neutral' },
};

const REASON_LABEL = {
  COMPRESSOR: 'Kompresör',
  DOOR: 'Kapı',
  LEAK: 'Sızıntı',
  ELECTRICAL: 'Elektrik',
  NOT_COOLING: 'Soğutmuyor',
  OTHER: 'Diğer',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

function formatDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}

function parsePhotoUrls(json) {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function statusBadgeFor(status) {
  const meta = STATUS_META[status];
  return <StatusBadge tone={meta?.tone || 'neutral'}>{meta?.label || status || '—'}</StatusBadge>;
}
function urgencyBadgeFor(urgency) {
  const meta = URGENCY_META[urgency];
  return <StatusBadge tone={meta?.tone || 'neutral'}>{meta?.label || urgency || '—'}</StatusBadge>;
}

// Salt-okunur değer kutusu (dps-ff görünümüyle uyumlu, edit input'larıyla aynı hizada).
const readBoxStyle = {
  display: 'flex', alignItems: 'center', minHeight: 46,
  background: '#F6F8FB', border: '1.5px solid #E5EBF2', borderRadius: 11,
  padding: '0 14px', fontSize: 14, fontWeight: 700, color: '#0E2A47',
  wordBreak: 'break-word',
};
function ReadField({ label, children, span }) {
  return (
    <FieldCustom label={label} span={span}>
      <div style={readBoxStyle}>{children}</div>
    </FieldCustom>
  );
}

export function CoolerFaultDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new'; // Bu entity'de "Yeni" desteklenmez; yine de standart için tutulur.
  const { canAdd, canEdit, canDelete, canCopy } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta('Soğutucu Arızası Detayı', 'Saha temsilcisinin mobile\'dan açtığı arıza bildirimi; servis takip no ve durum yönetimi.');

  const [row, setRow] = useState(location.state?.row || null);
  const [loading, setLoading] = useState(!location.state?.row && !isNew);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [audit, setAudit] = useState(null);

  // Editlenebilir alanlar (yalnız TrackingNo + Status).
  const [editTrackingNo, setEditTrackingNo] = useState('');
  const [editStatus, setEditStatus] = useState('Open');

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const applyRow = useCallback((r) => {
    setRow(r);
    setEditTrackingNo(pick(r, 'trackingNo', 'TrackingNo') || '');
    setEditStatus(pick(r, 'status', 'Status') || 'Open');
    setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
  }, []);

  // GET-by-id yok → listeyi çekip id ile bul (CoolerFaultsPage deseni).
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}?take=${FETCH_TAKE}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Kayıt alınamadı (${res.status})`);
      const data = unwrap(await res.json());
      const items = pick(data, 'items', 'Items') ?? [];
      const found = (Array.isArray(items) ? items : []).find((r) => String(pick(r, 'id', 'Id')) === String(id));
      if (!found) throw new Error('Arıza bildirimi bulunamadı.');
      applyRow(found);
    } catch (err) {
      showToast('error', err.message || 'Kayıt alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, applyRow, showToast]);

  // location.state.row varsa onu kullan; yoksa listeden yükle.
  useEffect(() => {
    if (location.state?.row) {
      applyRow(location.state.row);
      setLoading(false);
      // Nav-state satırı (liste $select) audit alanlarını içermez → tam kaydı arka planda
      // (bloklamadan) listeden bulup audit'i tazele (GET-by-id yok).
      fetch(`${API_BASE}?take=${FETCH_TAKE}`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => {
          if (!body) return;
          const data = unwrap(body);
          const items = pick(data, 'items', 'Items') ?? [];
          const rec = (Array.isArray(items) ? items : []).find((r) => String(pick(r, 'id', 'Id')) === String(id));
          if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        })
        .catch(() => {});
    } else if (!isNew) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const persist = async () => {
    if (isNew || !row) return;
    const recId = pick(row, 'id', 'Id');
    setSaving(true);
    try {
      const body = { trackingNo: editTrackingNo.trim() || null, status: editStatus };
      const res = await fetch(`${API_BASE}/${recId}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(body),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(payload, 'Message', 'message') || `İşlem başarısız (${res.status})`);
      showToast('success', 'Bildirim güncellendi.');
      // Yerel görünümü güncelle (GET-by-id yok; yeni değerleri elde tut).
      setRow((c) => ({ ...c, trackingNo: editTrackingNo.trim() || null, status: editStatus }));
      setTimeout(() => navigate('/cooler-faults'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const photoUrls = row ? parsePhotoUrls(pick(row, 'photoUrlsJson', 'PhotoUrlsJson')) : [];

  const barcode = pick(row, 'coolerBarcode', 'CoolerBarcode');
  const customerTitle = pick(row, 'customerTitle', 'CustomerTitle');
  const reason = pick(row, 'reason', 'Reason');
  const urgency = pick(row, 'urgency', 'Urgency');
  const affectedDoor = pick(row, 'affectedDoor', 'AffectedDoor');
  const note = pick(row, 'note', 'Note');
  const reportedAt = pick(row, 'reportedAt', 'ReportedAt');
  const createdDate = pick(row, 'createdDate', 'CreatedDate');
  const updatedDate = pick(row, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate');

  const statusBadge = row ? statusBadgeFor(pick(row, 'status', 'Status')) : null;

  const editForm = { trackingNo: editTrackingNo, status: editStatus };
  const setEdit = (k, v) => {
    if (k === 'trackingNo') setEditTrackingNo(v);
    else if (k === 'status') setEditStatus(v);
  };

  const actions = (
    <>
      <button className="dps-btn ghost" onClick={() => navigate('/cooler-faults')} disabled={saving}>İptal</button>
      {canSave && (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={persist}
          disabled={saving || !row}
        />
      )}
    </>
  );

  return (
    <DetailShell
      crumb={[
        { label: 'Soğutucu Arızaları', to: '/cooler-faults' },
        { label: 'Arıza Detayı', current: true },
      ]}
      status={statusBadge}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'CoolerFaultReport', entityId: id }}
      actions={actions}
      loading={loading}
    >
      {!row ? (
        <div className="dps-empty">
          <Icon name="info" size={34} />
          <p>Kayıt bulunamadı</p>
          <span>Listeye dönüp tekrar açmayı deneyin.</span>
        </div>
      ) : (
        <div className="dps-form">
          <Section icon="box" title="Arıza Bilgileri" subtitle="Mobile'dan gelen orijinal bildirim (salt okunur)">
            <Grid cols={2}>
              <ReadField label="Cihaz Barkodu">{barcode || '—'}</ReadField>
              <ReadField label="Nokta">{customerTitle || '—'}</ReadField>
              <ReadField label="Sebep">{REASON_LABEL[reason] || reason || '—'}</ReadField>
              <ReadField label="Öncelik">{urgencyBadgeFor(urgency)}</ReadField>
              <ReadField label="Etkilenen Kapı">{affectedDoor != null ? `#${affectedDoor}` : 'Tüm cihaz'}</ReadField>
              <ReadField label="Bildirim Zamanı">{formatDate(reportedAt)}</ReadField>
              {note && <ReadField label="Saha Notu" span>{note}</ReadField>}
            </Grid>
          </Section>

          {photoUrls.length > 0 && (
            <Section icon="layers" title={`Fotoğraflar (${photoUrls.length})`} subtitle="Saha temsilcisinin yüklediği görseller">
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {photoUrls.map((url, idx) => (
                  <a
                    key={idx}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'block', width: 140, height: 105, borderRadius: 10,
                      border: '1px solid #E5EBF2', overflow: 'hidden', background: '#F6F8FB',
                    }}
                  >
                    <img
                      src={url}
                      alt={`foto ${idx + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML =
                          '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:#94a3b8;font-size:11px;">Açılamadı</div>';
                      }}
                    />
                  </a>
                ))}
              </div>
            </Section>
          )}

          <Section
            icon="briefcase"
            title="Servis Yönetimi"
            subtitle="Servis takip no atayın ve durumu güncelleyin"
            note="Bu bildirim mobile push ile oluşturulur; yalnızca Takip No ve Durum düzenlenebilir."
          >
            <Grid cols={2}>
              <FieldText form={editForm} set={setEdit} k="trackingNo" label="Takip No" mono placeholder="örn. SRV-2026-0142" />
              <FieldSelect form={editForm} set={setEdit} k="status" label="Durum" options={STATUS_OPTIONS} />
            </Grid>
          </Section>

          <Section icon="info" title="Kayıt Bilgileri" subtitle="Sistem alanları (salt okunur)">
            <Grid cols={2}>
              <ReadField label="Oluşturulma">{formatDate(createdDate)}</ReadField>
              <ReadField label="Son Güncelleme">{formatDate(updatedDate)}</ReadField>
            </Grid>
          </Section>
        </div>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default CoolerFaultDetailPage;
